/**
 * useSessionState Hook
 *
 * Manages Solomon session state (sessionId, mode, agent state).
 * Follows Unix philosophy: one file, one job.
 *
 * @example
 * const { sessionId, mode, setMode, resetSession } = useSessionState();
 */

import { useState, useCallback, useMemo } from 'react';

import type { SolomonAgentState } from 'types/solomon';

// ============================================================================
// Types
// ============================================================================

export type SolomonMode = 'recommendations' | 'chat';
// Re-export SolomonAgentState for convenience
export type { SolomonAgentState };

export interface UseSessionStateOptions {
  /** Initial mode */
  initialMode?: SolomonMode;
  /** Initial agent state */
  initialAgentState?: SolomonAgentState;
}

export interface UseSessionStateReturn {
  /** Current session ID for conversation continuity */
  sessionId: string | null;
  /** Set the session ID */
  setSessionId: (id: string | null) => void;
  /** Current operating mode */
  mode: SolomonMode;
  /** Set the operating mode */
  setMode: (mode: SolomonMode) => void;
  /** Current agent execution state */
  agentState: SolomonAgentState;
  /** Set the agent state */
  setAgentState: (state: SolomonAgentState) => void;
  /** Reset session to initial state */
  resetSession: () => void;
  /** Whether currently in chat mode */
  isChatMode: boolean;
  /** Whether agent is busy (not idle) */
  isAgentBusy: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useSessionState({
  initialMode = 'recommendations',
  initialAgentState = 'idle',
}: UseSessionStateOptions = {}): UseSessionStateReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [mode, setModeInternal] = useState<SolomonMode>(initialMode);
  const [agentState, setAgentState] = useState<SolomonAgentState>(initialAgentState);

  // Set mode and handle transitions
  const setMode = useCallback((newMode: SolomonMode) => {
    setModeInternal(newMode);
  }, []);

  // Reset session to initial state
  const resetSession = useCallback(() => {
    setSessionId(null);
    setModeInternal(initialMode);
    setAgentState(initialAgentState);
  }, [initialMode, initialAgentState]);

  // Derived state
  const isChatMode = mode === 'chat';
  const isAgentBusy = agentState !== 'idle';

  return useMemo(
    () => ({
      sessionId,
      setSessionId,
      mode,
      setMode,
      agentState,
      setAgentState,
      resetSession,
      isChatMode,
      isAgentBusy,
    }),
    [sessionId, mode, setMode, agentState, resetSession, isChatMode, isAgentBusy]
  );
}

export default useSessionState;
